// FILL ERROR STYLE — the one spec for "this segment's image fill failed to
// load". Pure, no DOM (parity with emptyWedgeStyle.js's discipline).
//
// PROBLEM, ROUND 1 (gv-0914-dev-two-wedges-fill-error-flat-colour, wheel/4908
// seg 37102/37104, Gordon: "Segment 2 has a broken image"): a dead image/gif
// fill degraded to a flat fallback color indistinguishable from a segment the
// user deliberately painted flat. Round 1 fixed that with a red
// circle-and-"!" badge shown to EVERYONE.
//
// PROBLEM, ROUND 2 (gordon-0920-view-wheel-segment-title-labels-must-never-
// overlap-the-wheel-and-broken-image-mark, owner answer 2026-09-20 08:13 PT):
// that red alarm mark is wrong for a GUEST — "it's looking very broken for
// GUESTS, whereas the (!) really is more of an indicator for the Creator to
// fix something." Two changes:
//   (B1) the FALLBACK ITSELF is no longer a flat swatch, for anyone — it's
//        the same soft two-stop gradient (this segment's own `color` ->
//        its `innerColor`/`inner_color`, e.g. the categoryColors palette's
//        outer/inner pair) a normal colour segment can carry, so a broken
//        image reads as an intentionally-styled wedge, never a glitch. See
//        fillErrorGradientStops() below; fills.js paints it via the SAME
//        gradient machinery buildFillPaint's own "gradient" case already
//        has (no new paint code, no new visual language a wedge couldn't
//        otherwise produce).
//   (B2) the BADGE overlay (this file's icon geometry) is OWNER-ONLY (gated
//        in engine.js on `options.viewerIsOwner`) and is no longer an alarm:
//        a calm line-art "image off" glyph in a white circle — product
//        voice, not a red warning — with a 32px minimum hit area regardless
//        of how small the icon itself renders, so it stays reachable on a
//        thin ring. `data-fill-error="true"` stays on the wedge for BOTH
//        viewers (QA/e2e hook only — never painted).
//
// Sizing is derived from the wedge's own radial thickness (outerRadius -
// innerRadius), not a fixed px value, so the badge stays proportionate on a
// tiny thumbnail and a full-size View Wheel alike — same reasoning as
// HAIRLINE_STROKE_WIDTH/EMPTY_WEDGE_OUTLINE_WIDTH scaling with the wedge
// geometry rather than the viewport.

import { toHex, iyMix, PILL_INK } from "../components/D3Wheels/pillColors";
import { fontFamily, typography, radius, spacing, field, shadow } from "../theme/tokens";

/** Badge outer-circle (visual icon) radius, as a fraction of wedge radial thickness. */
const FILL_ERROR_ICON_THICKNESS_RATIO = 0.34;

/** Clamp so the badge never disappears on a razor-thin ring or swallows a
 *  whole wedge on a fat one. */
export const FILL_ERROR_ICON_MIN_RADIUS = 7;
export const FILL_ERROR_ICON_MAX_RADIUS = 16;

// B4/owner-hit-target law: "32 px hit area" — a real circle, independent of
// how small FILL_ERROR_ICON_MIN_RADIUS draws the visible glyph, so the badge
// stays a legal touch/click target even on the thinnest ring.
export const FILL_ERROR_HIT_RADIUS = 16; // 32px diameter

/** Badge backdrop — white disc so the glyph reads over any fallback color,
 *  including a dark one. */
export const FILL_ERROR_ICON_BG = "#ffffff";

/** Glyph + ring color — calm neutral ink (product voice), NEVER red — this is
 *  a fix-me note for the owner, not an alarm. Same canonical ink the rim-pill
 *  label text already uses (PILL_INK), so it reads as "this app's UI",
 *  not a second, drifting copy of the token. */
export const FILL_ERROR_ICON_COLOR = PILL_INK;

/** Accessible name (B2 law, verbatim). */
export const FILL_ERROR_ACCESSIBLE_NAME = "Image could not load";

/** Tooltip copy (B2 law, verbatim): what happened + what to do. */
export const FILL_ERROR_TOOLTIP_TEXT = "This image did not load. Select to replace it.";

/**
 * Badge circle radius for a wedge whose ring thickness is
 * `outerRadius - innerRadius`.
 */
export function fillErrorIconRadius(outerRadius, innerRadius) {
  const thickness = Math.max(0, (+outerRadius || 0) - (+innerRadius || 0));
  const r = thickness * FILL_ERROR_ICON_THICKNESS_RATIO;
  return Math.min(FILL_ERROR_ICON_MAX_RADIUS, Math.max(FILL_ERROR_ICON_MIN_RADIUS, r));
}

// Moderate lighten toward white for a derived "inner" gradient stop when a
// fill has no explicit second colour to pair with its own `color` — softer
// than colorsFor()'s 0.9 pill-tint mix (which is deliberately near-white,
// fine for a faint label background but too washed out to read as a wedge's
// own second gradient stop).
const DERIVED_INNER_MIX_TOWARD_WHITE = 0.35;

/**
 * The two stops for a failed image/gif fill's gradient fallback (B1):
 * `fill.color` (the segment's own configured/designed colour) paired with
 * `fill.innerColor` / `fill.inner_color` when the fill spec carries one
 * (e.g. a categoryColors palette's outer/inner pair), else a derived lighter
 * shade of the same hue — pure, no DOM, so it's unit-testable in isolation.
 * Falls back to a neutral grey pair when there is no usable colour at all
 * (never throws, matches fills.js's own `fallbackColor` null-safety).
 */
export function fillErrorGradientStops(fill) {
  const rawBase = (fill && fill.color) || "#c9c6c5";
  const base = toHex(rawBase) || rawBase;
  const rawInner = fill && (fill.innerColor || fill.inner_color);
  const inner = toHex(rawInner) || (toHex(base) ? iyMix(base, "#ffffff", DERIVED_INNER_MIX_TOWARD_WHITE) : base);
  return [base, inner];
}

// gordon-0923-P1-view-wheel-broken-image-callout-on-brand-box: the tooltip
// text (FILL_ERROR_TOOLTIP_TEXT above) was, until now, appended to the DOM
// with NO box at all — no radius, no background, no border, no app font —
// so it rendered as raw text in the browser's fallback serif/sans font,
// floating loose over the wheel. Every value below is read from the app's
// single design-token source (src/theme/tokens.js) rather than a new
// literal, so this callout can never visually drift from the rest of the
// app. `field.ink`/`field.bg`/`field.border` are the SAME tokens the app's
// shared form-field + tooltip surfaces already use (`field.ink` ==
// `PILL_INK`, both "#123051"), and `typography.text` is the app's shared
// small-text scale (12px / 1.5 line-height) — the "app body font, small-text
// size + line-height" the note calls for.
export const FILL_ERROR_TOOLTIP_MAX_WIDTH = 220;

export const FILL_ERROR_TOOLTIP_STYLE = {
  display: "flex",
  alignItems: "flex-start",
  gap: `${spacing.xs}px`,
  boxSizing: "border-box",
  maxWidth: `${FILL_ERROR_TOOLTIP_MAX_WIDTH}px`,
  padding: `${spacing.sm}px ${spacing.md}px`,
  borderRadius: `${radius.md}px`,
  background: field.bg,
  border: `1px solid ${field.border}`,
  boxShadow: shadow.e2,
  fontFamily,
  fontSize: `${typography.text.fontSize}px`,
  lineHeight: String(typography.text.lineHeight),
  color: field.ink,
};

/** Small line-art "image off" glyph size inside the tooltip box (px, square viewBox). */
export const FILL_ERROR_TOOLTIP_ICON_SIZE = 14;

/**
 * Keep the tooltip fully inside the wheel's own host container (never
 * overflowing the wheel, per Gordon's rule) by clamping its top-left corner
 * so `left/top + width/height` never exceeds `hostWidth/hostHeight`, and
 * never goes negative. Pure — no DOM — so the geometry is unit-testable
 * without a real layout engine (jsdom never lays anything out).
 */
export function clampFillErrorTooltipPosition(
  left,
  top,
  width,
  height,
  hostWidth,
  hostHeight,
  margin = 4
) {
  const maxLeft = Math.max(margin, hostWidth - width - margin);
  const maxTop = Math.max(margin, hostHeight - height - margin);
  return {
    left: Math.min(Math.max(margin, left), maxLeft),
    top: Math.min(Math.max(margin, top), maxTop),
  };
}
